@use "sizes";
@use 'style/utils';

$_CLASS: '.joy-button';
$_space-around-cta: 2px;

/**
Generic buttons style
 */

:host {
    --button-font-family: var(--joy-font-family-base);

    --button-bgcolor-disabled: var(--joy-color-neutral-30);
    --button-color-disabled: var(--joy-color-neutral-40);
    --button-border-color-disabled: var(--joy-color-neutral-30);
    --button-icon-color-disabled: var(--joy-color-neutral-40);

    display: inline-flex;
}

#{$_CLASS} {
    display: inline-flex;
    position: relative;
    justify-content: center;
    align-items: center;
    flex-wrap: nowrap;
    white-space: nowrap;
    box-sizing: border-box;
    font-family: var(--button-font-family);
    line-height: 1;
    z-index: 1;
    border: none;
    transition: all var(--joy-transition-duration-default);
    text-decoration: none;
    text-align: center;
    cursor: pointer;
    outline: none;
    opacity: 1;
    width: 100%;
    background-color: var(--button-bgcolor);
    color: var(--button-color, white);
    border: 2px solid var(--button-bdcolor, transparent);
    overflow: hidden;

    @include utils.rippleEffect();

    &:hover {
        color: var(--button-color-hover, var(--button-color, white));
        background-color: var(--button-bgcolor-hover, var(--button-bgcolor));
        border-color: var(--button-bdcolor-hover, var(--button-bgcolor-hover, --button-bgcolor));
    }

    &:active {
        color: var(--button-color-focus, var(--button-color-hover, var(--button-color, white)));
        background-color: var(--button-bgcolor-focus, var(--button-bgcolor-hover, var(--button-bgcolor)));
        border-color: var(--button-bdcolor-focus, var(--button-bgcolor-focus, var(--button-bgcolor-hover, var(--button-bgcolor))));
    }

    .joy-button_icon {
        color: inherit;
    }

    .joy-button_icon--left {
        margin-right: var(--joy-core-spacing-2);
    }

    .joy-button_icon--right {
        margin-left: var(--joy-core-spacing-2);
    }

    &:focus {
        box-shadow: inset 0 0 0 2px white;
        border-color: var(--button-bdcolor-focus, var(--joy-form-field-focus-color));
    }

    * {
        // ensure the click is always captured by the button and not its content (for example an SVG icon)
        pointer-events: none;
    }

    @include utils.disabledStateOnly {
        background-color: var(--button-bgcolor-disabled);
        color: var(--button-color-disabled);
        border-color: var(--button-border-color-disabled);
        cursor: not-allowed;

        joy-icon {
            color: var(--button-icon-color-disabled);
        }
    }

    joy-spinner {
        position: absolute;
        margin: auto;
    }

    &#{$_CLASS}_circle {
        padding: 0;
        min-width: auto;
        border-radius: var(--joy-core-radius-6);

        joy-icon {
            margin: 0;
        }

        #{$_CLASS}--slot {
            display: none;
        }
    }

    &#{$_CLASS}_loading:disabled {
        cursor: not-allowed;
        color: transparent;

        &:hover {
            color: transparent;
        }

        joy-icon {
            color: transparent;
        }
    }
}

#{$_CLASS}_main {
    --button-bgcolor: var(--joy-color-primary-50);
    --button-bdcolor: var(--button-bgcolor);
    --button-bgcolor-hover: var(--joy-color-primary-70);
    --button-bgcolor-focus: var(--joy-color-primary-90);
}

#{$_CLASS}_primary {
    --button-bgcolor: var(--joy-color-secondary-30);
    --button-bdcolor: var(--button-bgcolor);
    --button-bdcolor-focus: var(--joy-form-field-focus-color);
    --button-bgcolor-hover: var(--joy-color-secondary-50);
    --button-bgcolor-focus: var(--joy-color-secondary-90);
}

#{$_CLASS}_secondary {
    --ripple-background: var(--joy-color-neutral-50);

    --button-color: var(--joy-color-secondary-50);
    --button-bgcolor: transparent;
    --button-bdcolor: var(--button-color);
    --button-bdcolor-hover: var(--button-color);
    --button-bgcolor-hover: var(--joy-color-secondary-10);

    @include utils.disabledStateOnly() {
        --button-color: var(--joy-color-neutral-40);
        --button-bordercolor: var(--joy-color-neutral-30);
        --button-bgcolor: white;
    }
}

#{$_CLASS}_white {
    --ripple-background: var(--joy-color-neutral-50);

    --button-color: var(--joy-color-secondary-50);
    --button-color-focus: var(--joy-color-secondary-90);
    --button-bgcolor: white;
    --button-bgcolor-hover: var(--joy-color-neutral-20);
    --button-icon-color-white-hover: var(--joy-color-secondary-90);

    &:not(:disabled):hover {
        joy-icon {
            color: var(--button-icon-color-white-hover);
        }
    }
}

#{$_CLASS}_ghost {
    --ripple-background: var(--joy-color-neutral-40);

    --button-color: var(--joy-color-secondary-50);
    --button-color-hover: var(--joy-color-secondary-50);
    --button-bgcolor: transparent;
    --button-bgcolor-hover: var(--joy-color-neutral-20);

    &:not(:disabled):hover {
        joy-icon {
            color: var(--button-color);
        }
    }

    &:disabled,
    &:disabled:hover {
        --button-color: transparent;
    }
}

#{$_CLASS}_admin {
    --button-bgcolor: var(--joy-color-quaternary-50);
    --button-bgcolor-hover: var(--joy-color-quaternary-70);
    --button-bgcolor-focus: var(--joy-color-quaternary-100);
    --button-bdcolor: var(--button-bgcolor);
    --button-color: var(--joy-color-neutral-60);
}

@include sizes.setSizes($_CLASS);